import { NoteType, PricingType } from 'src/api/api.generated'
import { ReservationFormData } from './reservation-new.from.hook'
import { CartEditableQuote, CartQuote, ReservationCartRow, ReservationNewFormData, ReservationNewReservedResourceFormData } from './reservation-new.type'
import { ReservationGroupUpsertGridRow, ReservationNewGroupFormData } from '../../reservation-group-upsert/form/reservation-group.types'
import { ReservationGroupFormData } from '../../reservation-group-upsert/form/reservation-group-new.form.hook'
import { range } from 'src/utils/utils'
import { addDays, compareDates } from 'src/utils/date'

export function reservationNewGroupCartAsFormData(
  start: Date,
  end: Date,
  tenantId: string,
  propertyId: string,
  originId: string,
  data: ReservationGroupFormData,
  reservationStateId: string,
  rows: ReservationGroupUpsertGridRow[],
  optionExpiration?: Date | null,
): ReservationNewGroupFormData {
  const formData: ReservationNewGroupFormData = {
    credit: data.credit,
    reservedResources: rows
      .map((row) => {
        const tmpRR: ReservationGroupUpsertGridRow[] = []
        for (let index = 0; index < row.resourceNumber; index++) {
          tmpRR.push(row)
        }
        return tmpRR
      })
      .flat()
      .map((row) => {
        const manualPriceIsRequired = row.totalPrice !== row.initialTotalPrice
        const manualDailyPriceIsRequired = row.averagePrice * row.nights !== row.totalPrice

        const childrenAgeRanges = row.occupancies.children
          .filter((c) => c.quantity > 0)
          .map(({ childId, quantity }) => {
            return {
              childId,
              quantity,
            }
          })
        return {
          cartRowId: row.id,
          rateId: row.groupingInfo.rateId,
          manualDailyPrices: manualDailyPriceIsRequired
            ? range(0, row.nights - 1).map((dateOffset) => {
                const nightDate = addDays(new Date(row.filters.start.getTime()), dateOffset)
                const price = () => {
                  const lastNight = addDays(new Date(row.filters.end.getTime()), -1)
                  const isLastNight = compareDates(nightDate, lastNight) >= 0
                  if (isLastNight) {
                    return row.averagePrice + (row.totalPrice - row.averagePrice * row.nights)
                  }
                  return row.averagePrice
                }

                return {
                  day: nightDate,
                  price: price(),
                }
              })
            : undefined,
          manualPrice: manualPriceIsRequired ? (!manualDailyPriceIsRequired ? row.averagePrice : undefined) : undefined,
          chargeCategoryId: row.groupingInfo.chargeCategoryId,
          resourceOccupancies: [
            {
              start: start,
              end: end,
              resourceTypeId: row.groupingInfo.resourceTypeId,
            },
          ],
          adults: row.occupancies.adults,
          children: row.occupancies.children.map((child) => child.quantity).reduce((acc, val) => acc + val, 0),
          childOccupancies: childrenAgeRanges.length > 0 ? childrenAgeRanges : null,
        }
      }),
    tenantId: tenantId,
    propertyId: [propertyId],
    originId: originId,
    isGroup: true,
    group: data.group,
    reference: {
      mainGuestId: data.customerPersonId,
      customerCompanyId: data.customerCompanyId,
      customerPersonId: data.customerPersonId,
    },
    reservationStateId: reservationStateId,
    optionExpiration: optionExpiration ? optionExpiration : undefined,
    disableAutomation: data.disableAutomation,
    notes:
      (data.notes ?? '').length > 0
        ? [
            {
              type: NoteType.RESERVATION,
              description: data.notes,
            },
          ]
        : null,
  }
  return formData
}

export function reservationNewCartAsFormData(
  tenantId: string,
  propertyId: string,
  originId: string,
  data: ReservationFormData,
  reservationStateId: string,
  rows: ReservationCartRow[],
  optionExpiration?: Date | null,
): ReservationNewFormData {
  const reservedResources: ReservationNewReservedResourceFormData[] = rows.map((row) => {
    const manualPriceIsRequired = row.totalPrice !== row.initialTotalPrice

    const manualDailyPricesIsRequired = manualPriceIsRequired && row.averagePrice * row.nights !== row.totalPrice

    // The prices of this list are calculated to obtain a sum equal to totalPrice and no decimals are lost or added in the rounding
    const dailyPricesRecalculated = manualDailyPricesIsRequired
      ? range(0, row.nights - 1).map((dateOffset) => {
          const nightDate = addDays(new Date(row.start.getTime()), dateOffset)
          const price = () => {
            const lastNight = addDays(new Date(row.end.getTime()), -1)
            const isLastNight = compareDates(nightDate, lastNight) >= 0
            if (isLastNight) {
              return row.averagePrice + (row.totalPrice - row.averagePrice * row.nights)
            }
            return row.averagePrice
          }

          return {
            day: nightDate,
            price: price(),
          }
        })
      : undefined

    return {
      cartRowId: row.id,
      rateId: row.rate.id,
      policy: row.policy,
      manualDailyPrices: manualDailyPricesIsRequired ? dailyPricesRecalculated : undefined,
      manualPrice: manualPriceIsRequired && !manualDailyPricesIsRequired ? row.averagePrice : undefined,
      chargeCategoryId: row.chargeCategory.id,
      resourceOccupancies: [
        {
          start: row.start,
          end: row.end,
          resourceTypeId: row.resourceType.id,
        },
      ],
      adults: row.occupancy.adults,
      children: row.occupancy.children.map((child) => child.quantity).reduce((acc, val) => acc + val, 0),
      childOccupancies: row.occupancy.children.length > 0 ? row.occupancy.children : null,
      additionalServices: row.addedAdditionalServices?.map((as) => {
        return {
          additionalServiceId: as.id,
          quantity: additionalServiceShouldHaveQuantity(as.pricingType) ? as.quantity ?? 1 : undefined,
        }
      }),
    }
  })

  const formData: ReservationNewFormData = {
    credit: data.credit,
    reservedResources: reservedResources,
    tenantId: tenantId,
    propertyId: [propertyId],
    originId: originId,
    isGroup: false,
    reference: {
      mainGuestId: data.customerPersonId,
      customerCompanyId: data.customerCompanyId,
      customerPersonId: data.customerPersonId,
    },
    reservationStateId: reservationStateId,
    optionExpiration: optionExpiration ? optionExpiration : undefined,
    disableAutomation: data.disableAutomation,
    notes:
      (data.notes ?? '').length > 0
        ? [
            {
              type: NoteType.RESERVATION,
              description: data.notes,
            },
          ]
        : null,
  }
  return formData
}

export function isCartQuote(value: CartQuote | CartEditableQuote | null | undefined): value is CartEditableQuote {
  if (value === null || value === undefined) {
    return false
  }
  return 'readOnly' in value
}

export function isCartEditableQuote(value: CartQuote | CartEditableQuote | null | undefined): value is CartEditableQuote {
  if (value === null || value === undefined) {
    return false
  }
  return !isCartQuote(value)
}

export function additionalServiceShouldHaveQuantity(additionalServicePricingType: PricingType): boolean {
  return additionalServicePricingType === PricingType.PER_STAY || additionalServicePricingType === PricingType.PER_NIGHT
}
